/**
 * Users — a searchable table of customers.
 *
 * Contact details are shown **in full** wherever the API supplies them. That is a deliberate
 * reversal of the platform's own masking rule and it is worth being explicit about: masking
 * exists so a compromised *customer* session cannot be used to harvest contact details, and an
 * operator session is a different thing entirely. It is held by a named person, behind a
 * separate credential, and the actions it exists for — phoning a customer back about a stuck
 * payout, matching a number against a bank's record — are exactly the ones a masked number makes
 * impossible. What pays for it is that every read here is behind the admin guard and audited.
 *
 * Where the API has not supplied a full number, the mask is shown *as a mask*, with a chip
 * saying so. A half-number presented as a whole one is worse than no number at all.
 *
 * A row drills into the customer record.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext.js';
import { SkeletonTable } from '../components/Skeleton.js';
import { Card, EmptyState, ErrorBox, SearchInput } from '../components/ui.js';
import { AdminAvatar } from '../components/AdminAvatar.js';
import { ContactPhone } from '../components/ContactPhone.js';
import { TierBadge, UserStatusBadge } from '../components/badges.js';
import { useAsync } from '../hooks/useAsync.js';
import { formatDate } from '../lib/format.js';

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export function UsersPage(): JSX.Element {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 250);
  const state = useAsync(() => api.listUsers(debounced), [debounced]);

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Users</h1>
        <p className="page-head__sub">
          Search customers by name, email, phone, tier or status. Open a record to review limits,
          move a balance or change the account state. Contact details are shown in full here, and
          every read is recorded against your name.
        </p>
      </div>

      <div className="toolbar">
        <div className="toolbar__grow">
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder="Search name, email, phone, tier, status…"
          />
        </div>
        {state.data !== null && <span className="faint">{state.data.items.length} shown</span>}
      </div>

      <Card flush>
        {state.loading ? (
          // Six columns and a screenful of rows. The frame, the search box and the page
          // heading all stay put, so the only thing that changes when the data lands is the
          // cells themselves.
          <SkeletonTable columns={6} rows={8} label="Loading customers" />
        ) : state.error !== null ? (
          <ErrorBox message={state.error} />
        ) : state.data === null || state.data.items.length === 0 ? (
          <EmptyState glyph="🔍" title="No users match" hint="Try a different search." />
        ) : (
          <div className="table-wrap">
            <table className="table table--rows-clickable">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Tier</th>
                  <th>Status</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {state.data.items.map((u) => (
                  <tr key={u.id} onClick={() => navigate(`/users/${u.id}`)}>
                    {/* `table__contact` keeps contact values whole: nowrap, sized to their
                        content, never ellipsised. A half-shown phone number or a clipped name is
                        worse than a wide table — support reads these aloud, and the table already
                        scrolls horizontally inside `.table-wrap`. */}
                    <td className="table__contact">
                      <div className="table__who">
                        <AdminAvatar
                          userId={u.id}
                          name={u.displayName}
                          avatar={u.avatar}
                          size={32}
                        />
                        <span style={{ fontWeight: 600 }}>{u.displayName ?? 'Unnamed customer'}</span>
                      </div>
                    </td>
                    <td className="mono table__contact">
                      {u.email ?? <span className="muted">–</span>}
                    </td>
                    <td className="table__contact">
                      {/* No copy button per row: twenty-four of them is noise, and the detail
                          page one row away has one. The masked chip stays, because that is the
                          thing an operator must not miss. */}
                      <ContactPhone row={u} copy={false} compact />
                    </td>
                    <td>
                      <TierBadge tier={u.tier} />
                    </td>
                    <td>
                      <UserStatusBadge status={u.status} />
                    </td>
                    <td className="muted">{formatDate(u.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
